import { useNavigate } from "react-router-dom";
import { IconBuildingBank } from "@tabler/icons-react";
import { Heading, Text } from "../../../../components/common/typography";
import { Button } from "../../../../components/common/button";
import { ChangeRequestStatusBadge } from "../../../change-requests/presentation/components/change-request-status-badge";
import { formatDate } from "../../../../utils/date";
import { AppRoutes } from "../../../../common/routes/app_routes";
import type { ChangeRequestDto } from "../../../../api";

interface BankingDetailsCardProps {
  changeRequest: ChangeRequestDto;
}

export function BankingDetailsCard({ changeRequest }: BankingDetailsCardProps) {
  const navigate = useNavigate();
  const proposed = changeRequest.proposedChanges as Record<string, unknown> | undefined;

  return (
    <div className="flex flex-col rounded-2xl border border-borderDefault bg-bgWhite shadow-[0px_2px_12px_0px_rgba(190,189,217,0.2)]">
      <div className="flex items-start justify-between px-6 pt-5 pb-3">
        <div className="flex items-center gap-2">
          <IconBuildingBank size={20} className="text-textMuted" />
          <Heading variant="h3" tone="dark">Bank Account</Heading>
        </div>
        <ChangeRequestStatusBadge status={changeRequest.status} />
      </div>

      <div className="flex flex-col gap-2 px-6 pb-4">
        <DetailRow label="Bank Name" value={proposed?.bankName} />
        <DetailRow label="Account Holder" value={proposed?.accountHolder} />
        <DetailRow label="IBAN" value={proposed?.iban} />
        {changeRequest.submittedAt && (
          <DetailRow label="Submitted" value={formatDate(changeRequest.submittedAt, true)} />
        )}
      </div>

      {changeRequest.status !== "APPROVED" && (
        <div className="border-t border-borderLight px-6 py-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              navigate(AppRoutes.ADMIN_CHANGE_REQUEST_REVIEW.replace(":id", String(changeRequest.id)))
            }
          >
            Review
          </Button>
        </div>
      )}
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: unknown }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <Text variant="bodySm" tone="muted">{label}</Text>
      <Text variant="bodyBold" tone="dark">{String(value ?? "—")}</Text>
    </div>
  );
}
